import { useCallback, useEffect } from 'react';
import UsageTracker from '../../../utils/UsageTracker';
import { SCREENS } from '../../../constants/Tracking';
import { MY_SAVED_ITEMS_PAGE_SIZE, PlaylistCreatedAndAssignedByLabel, SavedItemsPagedList } from '../../savedItems';
import { useMyPlaylistsFilters, useMyPlaylistsParams } from '../myPlaylistsHooks';
import { useMyPlaylists } from '../myPlaylistsQueries';
import { MyPlaylistsTabNavigation } from './MyPlaylistsTabNavigation';
import { MyPlaylistsTabNavigationMobile } from './MyPlaylistsTabNavigationMobile';
import { MY_PLAYLISTS_SORT_MAP, MY_PLAYLISTS_TABS_MAP_TO_INTERACTION } from '../myPlaylistsTypes';
import { useDisplayedData } from '../../table/tableHooks';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { FILTER_TYPES } from '../../../constants/TopFilterConstants';
import { useHasScope } from '../../accounts';
import { RouterLink } from '../../../components/AcademyLinks';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Box } from 'foundations-components/transitional/components/Box';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import { LearningContentCard } from '../../cards/components/LearningContentCard';
import { TruncateMultiline } from 'foundations-components/transitional/components/TruncateMultiline';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import styledWithTheme from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const useMyPlaylistsTab = () => {
  const {
    page: pageParam,
    tab
  } = useMyPlaylistsParams();
  const {
    searchFilter,
    sortBy
  } = useMyPlaylistsFilters();
  const page = Number(pageParam) || 1;
  const hasFilters = !!searchFilter && !!sortBy;
  const {
    sortProperty,
    sortOrder
  } = MY_PLAYLISTS_SORT_MAP[sortBy || (searchFilter ? 'RELEVANCE_DESCENDING' : 'RECENTLY_ACCESSED_DESCENDING')];
  const {
    totalPlaylists,
    playlists,
    playlistsLoading,
    playlistsError
  } = useMyPlaylists({
    interactionTypes: MY_PLAYLISTS_TABS_MAP_TO_INTERACTION[tab],
    limit: MY_SAVED_ITEMS_PAGE_SIZE,
    page,
    sortOrder,
    sortProperty,
    query: searchFilter
  });
  const {
    cardModels,
    cardModelsLoading,
    cardModelsError,
    userProgressDataLoading
  } = useLearningContentCardModels({
    items: (playlists === null || playlists === void 0 ? void 0 : playlists.map(({
      id,
      type,
      language,
      hiddenForOwner
    }) => ({
      id,
      type: type === 'LEARNING_PATH' ? 'LEARNING_PATH' : 'PLAYLIST',
      language,
      hiddenForOwner
    }))) || [],
    skip: !playlists
  });
  return {
    totalPlaylists,
    tab,
    page,
    playlists: cardModels,
    playlistsLoading: playlistsLoading || cardModelsLoading || userProgressDataLoading,
    playlistsError: playlistsError || cardModelsError,
    hasFilters,
    searchFilter,
    sortBy
  };
};
const ArchivedMessageWrapper = styledWithTheme.span(({
  theme
}) => ({
  '&::before': {
    content: '('
  },
  '&::after': {
    content: ')'
  },
  textTransform: 'none',
  marginBlockStart: theme.space['100'],
  marginInlineStart: theme.space['100'],
  opacity: 0.5,
  color: theme.color['text-core-on-fill-default']
}));
const HubspotPlaylistsAdminLink = () => {
  return /*#__PURE__*/_jsxs(RouterLink, {
    to: '/hubspot-playlists-admin',
    children: [/*#__PURE__*/_jsx(HelpIcon, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "learningCenter.playlists.hubspotPlaylists.admin.subtitle"
      }),
      tooltipPlacement: "left"
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "learningCenter.playlists.hubspotPlaylists.admin.heading"
    })]
  });
};
const AdminLinkWrapper = ({
  children
}) => {
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    children: [/*#__PURE__*/_jsx(View, {
      inlineSize: "75%",
      children: children
    }), /*#__PURE__*/_jsx(View, {
      inlineSize: "25%",
      children: /*#__PURE__*/_jsx(Flex, {
        justifyContent: "end",
        children: /*#__PURE__*/_jsx("div", {
          style: {
            whiteSpace: 'nowrap'
          },
          children: /*#__PURE__*/_jsx(HubspotPlaylistsAdminLink, {})
        })
      })
    })]
  });
};
export const MyPlaylistsTabsLegacy = () => {
  const {
    hasFilters,
    playlists,
    page,
    totalPlaylists,
    playlistsLoading,
    playlistsError,
    tab,
    searchFilter,
    sortBy
  } = useMyPlaylistsTab();
  const hasSearchFilter = !!(searchFilter && searchFilter.trim().length);
  const {
    isMobile
  } = useMediaQuery();

  // displays only initial data dependent on tab, page and filters in order
  // to render ghosts
  const displayedData = useDisplayedData({
    playlists,
    totalPlaylists
  }, {
    loading: playlistsLoading,
    initialOnly: true,
    parameters: {
      tab,
      page,
      searchFilter,
      sortBy
    }
  });
  useEffect(() => {
    UsageTracker.track('pageView', {
      screen: SCREENS.MY_PLAYLISTS
    });
  }, []);
  const isGhost = useCallback(playlistId => playlists !== null && !playlists.some(p => p.id === playlistId), [playlists]);
  const isArchived = useCallback(playlistId => tab !== 'archived' && playlists !== null && playlists.some(p => p.id === playlistId && p.hiddenForOwner), [playlists, tab]);
  const {
    hasScope: hasPublicPlaylistsAdminScope
  } = useHasScope('academy-public-playlists-admin');
  const isRequesting = playlistsLoading && !displayedData;
  const {
    playlists: playlistsToDisplay,
    totalPlaylists: displayedTotalPlaylists
  } = displayedData || {};
  return /*#__PURE__*/_jsx(SavedItemsPagedList, {
    alwaysShowFilters: hasSearchFilter,
    alwaysShowResultsStatusBar: true,
    error: playlistsError,
    filters: [FILTER_TYPES.SEARCH, FILTER_TYPES.SORT_BY_MY_PLAYLISTS],
    hasError: !!playlistsError,
    hasItems: !!displayedTotalPlaylists && displayedTotalPlaylists > 0 || hasFilters // having filters means there are playlists
    ,
    hideFilterLabel: true,
    isLoadingPageOnDemand: true,
    isRequesting: isRequesting,
    itemKey: "myPlaylists",
    list: playlistsToDisplay === null || playlistsToDisplay === void 0 ? void 0 : playlistsToDisplay.map(playlist => {
      const {
        id,
        description
      } = playlist;
      return /*#__PURE__*/_jsx(LearningContentCard, {
        body: /*#__PURE__*/_jsxs(_Fragment, {
          children: [tab !== 'created-by-me' && tab !== 'archived' && /*#__PURE__*/_jsx("div", {
            style: {
              opacity: isArchived(id) ? 0.5 : 1
            },
            children: /*#__PURE__*/_jsx(PlaylistCreatedAndAssignedByLabel, {
              marginBlockEnd: "200",
              playlistId: id
            })
          }), /*#__PURE__*/_jsx(TruncateMultiline, {
            maxChars: 150,
            text: description || ''
          })]
        }),
        isGhost: isGhost(id) || isArchived(id),
        item: playlist,
        overviewType: "my-playlists",
        renderThumbnailContentTypeName: type => /*#__PURE__*/_jsxs(_Fragment, {
          children: [type, isArchived(id) && /*#__PURE__*/_jsx(ArchivedMessageWrapper, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.myPlaylists.archived"
            })
          })]
        })
      }, id);
    }),
    markerKey: "MY_PLAYLISTS",
    mobileResultsActions: !hasSearchFilter && /*#__PURE__*/_jsx(MyPlaylistsTabNavigationMobile, {}),
    navigation: !isMobile && /*#__PURE__*/_jsx(TFlex, {
      children: /*#__PURE__*/_jsx(Box, {
        grow: 1,
        shrink: 1,
        children: /*#__PURE__*/_jsx(MyPlaylistsTabNavigation, {
          Wrapper: hasPublicPlaylistsAdminScope ? AdminLinkWrapper : undefined
        })
      })
    }),
    page: page,
    totalCount: displayedTotalPlaylists || 0
  });
};